import Vue from 'vue'
import Vuex from 'vuex'
import { PERM_TREE } from '@/utils/permission'
import { FALLBACK_MENU } from '@/utils/menu'
import { permissionApi } from '@/api/biz'

Vue.use(Vuex)

function loadPeriod() {
  try {
    return localStorage.getItem('xp_period') || 'year'
  } catch (e) {
    return 'year'
  }
}

function loadPeriodRange() {
  try {
    const raw = localStorage.getItem('xp_period_range')
    return raw ? JSON.parse(raw) : { start: '', end: '' }
  } catch (e) {
    return { start: '', end: '' }
  }
}

function loadUser() {
  try {
    const raw = localStorage.getItem('xp_user')
    return raw ? JSON.parse(raw) : { name: '', avatar: '' }
  } catch (e) {
    return { name: '', avatar: '' }
  }
}

const savedRange = loadPeriodRange()

export default new Vuex.Store({
  state: {
    token: localStorage.getItem('xp_token') || '',
    user: loadUser(),
    menuNav: FALLBACK_MENU.slice(),
    menuModules: [],
    menusLoaded: false,
    permTree: PERM_TREE,
    dictCache: {},
    period: loadPeriod(),
    periodStart: savedRange.start || '',
    periodEnd: savedRange.end || ''
  },
  getters: {
    visibleMenus: (state) => {
      const nav = state.menuNav.length ? state.menuNav : FALLBACK_MENU
      const user = state.user || {}
      if (user.isSuper) return nav
      const perms = user.permCodes || []
      if (!perms.length) return nav.filter((m) => m.key === 'dashboard' || m.group)
      const granted = {}
      perms.forEach((p) => {
        if (p.indexOf('menu:') === 0) granted[p.slice(5)] = 1
      })
      const visible = nav.filter((m) => {
        if (m.group) return true
        return granted[m.key]
      })
      return visible.filter((m, i) => {
        if (!m.group) return true
        for (let j = i + 1; j < visible.length; j++) {
          if (visible[j].group) break
          return true
        }
        return false
      })
    }
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      if (token) localStorage.setItem('xp_token', token)
      else localStorage.removeItem('xp_token')
    },
    SET_USER(state, user) {
      state.user = user
      localStorage.setItem('xp_user', JSON.stringify(user))
    },
    SET_MENUS(state, { nav, modules, loaded = true }) {
      state.menuNav = nav && nav.length ? nav : FALLBACK_MENU.slice()
      state.menuModules = modules || []
      state.menusLoaded = loaded !== false
    },
    SET_PERIOD(state, period) {
      state.period = period
      try { localStorage.setItem('xp_period', period) } catch (e) { /* ignore */ }
    },
    SET_PERIOD_RANGE(state, { start, end }) {
      state.periodStart = start || ''
      state.periodEnd = end || ''
      try {
        localStorage.setItem('xp_period_range', JSON.stringify({ start: state.periodStart, end: state.periodEnd }))
      } catch (e) { /* ignore */ }
    },
    SET_DICT(state, { type, items }) {
      Vue.set(state.dictCache, type, items)
    }
  },
  actions: {
    login({ commit }, { token, user }) {
      commit('SET_TOKEN', token)
      commit('SET_USER', user || { name: '管理员', avatar: '管' })
    },
    logout({ commit }) {
      commit('SET_TOKEN', '')
      commit('SET_USER', { name: '', avatar: '' })
      commit('SET_MENUS', { nav: FALLBACK_MENU.slice(), modules: [], loaded: false })
      try {
        localStorage.removeItem('xp_role')
        localStorage.removeItem('xp_roleperms')
      } catch (e) { /* ignore */ }
    },
    async loadMenus({ commit, state }) {
      if (state.menusLoaded) return
      try {
        const res = await permissionApi.menuTree()
        const data = res && res.data ? res.data : {}
        commit('SET_MENUS', {
          nav: data.nav || FALLBACK_MENU.slice(),
          modules: data.modules || []
        })
      } catch (e) {
        commit('SET_MENUS', { nav: FALLBACK_MENU.slice(), modules: [] })
      }
    },
    async refreshUser({ commit, state }) {
      if (!state.token) return
      try {
        const { authApi } = await import('@/api/biz')
        const res = await authApi.profile()
        const data = res && res.data
        if (!data) return
        const prev = state.user || {}
        commit('SET_USER', {
          name: data.realName || data.username || prev.name,
          avatar: (data.realName || data.username || prev.name || '管').slice(0, 1),
          userId: data.userId,
          username: data.username,
          roleCode: data.roleCode,
          isSuper: data.isSuper === 1,
          dataScope: data.dataScope,
          permCodes: data.permCodes || [],
          mustChangePassword: !!data.mustChangePassword
        })
      } catch (e) { /* ignore */ }
    },
    setPeriod({ commit }, period) {
      commit('SET_PERIOD', period)
    },
    setPeriodRange({ commit }, range) {
      commit('SET_PERIOD_RANGE', range)
    },
    cacheDict({ commit }, payload) {
      commit('SET_DICT', payload)
    }
  }
})
